import { useEffect, useMemo, useState } from "react";
import { X, Pencil } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ModalPortal";
import { useAuth } from "@/context/AuthContext";

/**
 * Modal to adjust a time report after the fact.
 * - Admins: kan justera tid, objekt, moment, anteckning och avvikelse.
 * - Anställda: kan endast justera avvikelse-fältet på sin egen rapport.
 * Shows live-computed duration.
 */
export default function EditReportModal({ report, objects, tasks, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    start_local: toLocalInput(report?.start_time),
    end_local: toLocalInput(report?.end_time),
    object_id: report?.object_id || "",
    task_id: report?.task_id || "",
    note: report?.note || "",
    deviation: report?.deviation || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      start_local: toLocalInput(report?.start_time),
      end_local: toLocalInput(report?.end_time),
      object_id: report?.object_id || "",
      task_id: report?.task_id || "",
      note: report?.note || "",
      deviation: report?.deviation || "",
    });
  }, [report]);

  const computedSeconds = useMemo(() => {
    if (!form.start_local || !form.end_local) return null;
    const s = new Date(form.start_local);
    const e = new Date(form.end_local);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
    const diff = Math.floor((e - s) / 1000);
    return diff;
  }, [form.start_local, form.end_local]);

  const invalidRange =
    computedSeconds !== null && computedSeconds <= 0;

  const save = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      // Anställd — bara deviation. Backend accepterar endast egna rapporter.
      setSaving(true);
      try {
        const r = await api.patch(`/reports/${report.report_id}`, {
          deviation: form.deviation || "",
        });
        toast.success((form.deviation || "").trim() ? "Avvikelse sparad" : "Avvikelse rensad");
        onSaved?.(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Kunde inte spara avvikelse");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!form.start_local) {
      toast.error("Starttid saknas");
      return;
    }
    if (!form.end_local) {
      toast.error("Sluttid saknas");
      return;
    }
    if (invalidRange) {
      toast.error("Sluttiden måste vara efter starttiden");
      return;
    }
    if (!form.object_id) {
      toast.error("Välj ett objekt");
      return;
    }
    if (!form.task_id) {
      toast.error("Välj ett moment");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        start_time: new Date(form.start_local).toISOString(),
        end_time: new Date(form.end_local).toISOString(),
        object_id: form.object_id,
        task_id: form.task_id,
        note: form.note || "",
        deviation: form.deviation || "",
      };
      const r = await api.patch(`/reports/${report.report_id}`, payload);
      toast.success("Rapport justerad");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara ändringen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="edit-report-modal"
      onClick={onClose}
    >
      <div
        className="tk-card w-full max-w-xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
              <Pencil size={14} /> Justera rapport
            </div>
            <h2 className="font-heading text-2xl mt-1">{report?.user_name}</h2>
            <p className="text-xs text-text-muted mt-0.5">
              {new Date(report?.start_time).toLocaleDateString("sv-SE", {
                timeZone: "Europe/Stockholm",
                dateStyle: "full",
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-report-modal-close"
            className="text-text-muted hover:text-text-strong"
            aria-label="Stäng"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={save} className="space-y-4">
          {isAdmin ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
                  Starttid
                  <input
                    type="datetime-local"
                    value={form.start_local}
                    onChange={(e) => setForm({ ...form, start_local: e.target.value })}
                    className="tk-input"
                    data-testid="edit-report-start"
                    required
                  />
                </label>
                <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
                  Sluttid
                  <input
                    type="datetime-local"
                    value={form.end_local}
                    onChange={(e) => setForm({ ...form, end_local: e.target.value })}
                    className="tk-input"
                    data-testid="edit-report-end"
                    required
                  />
                </label>
              </div>

              <div
                className={`text-sm tabular-nums px-3 py-2 rounded border ${
                  invalidRange
                    ? "border-rose-300 bg-rose-50 text-rose-900"
                    : "border-app bg-surface-hover text-text-muted"
                }`}
                data-testid="edit-report-duration"
              >
                {invalidRange
                  ? "Sluttiden måste vara efter starttiden"
                  : computedSeconds !== null
                  ? `Varaktighet: ${formatSeconds(computedSeconds)}`
                  : "Ange start- och sluttid för att se varaktighet"}
              </div>

              <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
                Objekt
                <select
                  value={form.object_id}
                  onChange={(e) => setForm({ ...form, object_id: e.target.value })}
                  className="tk-input"
                  data-testid="edit-report-object"
                  required
                >
                  <option value="">— välj objekt —</option>
                  {objects.map((o) => (
                    <option key={o.object_id} value={o.object_id}>
                      {o.name}
                      {o.status === "archived" ? " (arkiverat)" : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
                Moment
                <select
                  value={form.task_id}
                  onChange={(e) => setForm({ ...form, task_id: e.target.value })}
                  className="tk-input"
                  data-testid="edit-report-task"
                  required
                >
                  <option value="">— välj moment —</option>
                  {tasks.map((t) => (
                    <option key={t.task_id} value={t.task_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
                Anteckning
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={3}
                  className="tk-input"
                  data-testid="edit-report-note"
                  placeholder="Valfri anteckning (t.ex. 'Glömde stämpla ut — justerat manuellt')"
                />
              </label>
            </>
          ) : (
            // Anställd-vy — read-only sammanfattning + avvikelse-fältet
            <div className="text-sm space-y-1 border border-app rounded-lg p-3 bg-surface-hover">
              <div><span className="text-text-muted">Objekt:</span> {report?.object_name}</div>
              <div><span className="text-text-muted">Moment:</span> {report?.task_name}</div>
              <div>
                <span className="text-text-muted">Tid:</span>{" "}
                {formatDateTime(report?.start_time)}
                {report?.end_time ? ` – ${formatDateTime(report.end_time)}` : ""}
              </div>
              {computedSeconds !== null && (
                <div><span className="text-text-muted">Varaktighet:</span> {formatSeconds(computedSeconds)}</div>
              )}
              {report?.note && (
                <div className="pt-1 border-t border-app mt-2">
                  <span className="text-text-muted">Anteckning:</span> {report.note}
                </div>
              )}
              <div className="pt-1 text-[11px] text-text-muted">
                Bara admin kan ändra tid, objekt eller moment. Kontakta din administratör om något är fel.
              </div>
            </div>
          )}

          <label className="text-xs uppercase tracking-wider text-[var(--brand-rust)] flex flex-col gap-1">
            <span className="flex items-center gap-1.5">
              <span aria-hidden>⚠</span> Avvikelse på momentet
            </span>
            <textarea
              value={form.deviation}
              onChange={(e) => setForm({ ...form, deviation: e.target.value })}
              rows={3}
              className="tk-input border-[var(--brand-rust)]/40 focus:border-[var(--brand-rust)]"
              data-testid="edit-report-deviation"
              placeholder="Fritext — beskriv vad som strulade i just detta moment (t.ex. 'Fel dimension på räckesstolpe, kunden godkände muntligt')"
            />
            <span className="text-[10px] normal-case tracking-normal text-text-muted">
              Kommer med i avvikelselistan för objektet.
            </span>
          </label>

          {report?.edited_at && (
            <div className="text-[11px] text-text-muted border-t border-app pt-3">
              Senast justerad {formatDateTime(report.edited_at)}
              {report.edited_by_name ? ` av ${report.edited_by_name}` : ""}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="tk-btn-outline"
              data-testid="edit-report-cancel"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="tk-btn-primary"
              disabled={saving || (isAdmin && invalidRange)}
              data-testid="edit-report-save"
            >
              {saving ? "Sparar…" : "Spara ändring"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // datetime-local needs YYYY-MM-DDTHH:mm in LOCAL time
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function formatSeconds(sec) {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      timeZone: "Europe/Stockholm",
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}
